/**
 * The to-do list of a proposal or publication: a card with the latest to-dos that opens the whole list. Each
 * to-do mirrors onto a board card (proposal/proposal/publication_sync.py), and assigning one notifies the
 * person (todo_assigned). Used beside the writer's sections, and in the reviews dialog for a round's revision
 * (reviewRoundId: only that round's to-dos, kept on its own "Revision" board).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Autocomplete, Box, Button, Checkbox, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton,
  InputAdornment, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleOutlineIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  LocalOffer as TagIcon,
  Task as TaskIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { fetchBoards } from 'shared/utils/projectBoardApi';

/* ===== Proposal Todo Panel ===== */

const TODO_PRIORITY = {
  high:   { label: 'High',   color: '#d32f2f' },
  medium: { label: 'Medium', color: '#ed6c02' },
  low:    { label: 'Low',    color: '#2e7d32' },
};

function TagInput({ tags, setForm }) {
  const [value, setValue] = useState('');

  const addTag = (raw) => {
    const tag = (raw ?? value).trim();
    if (!tag) return;
    setForm(f => f.tags.includes(tag) ? f : { ...f, tags: [...f.tags, tag] });
    setValue('');
  };

  const handleChange = (e) => {
    const val = e.target.value;
    if (val.endsWith(',')) {
      addTag(val.slice(0, -1));
    } else {
      setValue(val);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      addTag();
    } else if (e.key === 'Backspace' && !value && tags.length) {
      setForm(f => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };

  return (
    <TextField
      fullWidth placeholder="Add tag — press Enter or comma"
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      sx={{ mb: 1.5 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => addTag()} disabled={!value.trim()} tabIndex={-1}>
              <AddIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ),
        startAdornment: tags.length ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mr: 0.5, maxWidth: '60%' }}>
            {tags.map(tag => (
              <Chip key={tag} label={tag} size="small"
                onDelete={() => setForm(f => ({ ...f, tags: f.tags.filter(x => x !== tag) }))} />
            ))}
          </Box>
        ) : null,
      }}
    />
  );
}

function TodoForm({ form, setForm, sections, consortiumMembers, onSave, onCancel, boards, showBoard, fixedBoardName }) {
  return (
    <Box sx={{ p: 2, mb: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'primary.light', bgcolor: 'background.default' }}>
      <TextField
        fullWidth autoFocus placeholder="Task title"
        value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSave(); } }}
        sx={{ mb: 1.5 }}
      />
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
        <TextField
          select fullWidth label="Priority" value={form.priority}
          onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
          SelectProps={{ MenuProps: { sx: { zIndex: 5000 } } }}
        >
          {Object.entries(TODO_PRIORITY).map(([k, v]) => (
            <MenuItem key={k} value={k}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: v.color }} />
                {v.label}
              </Box>
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth label="Due date" type="date" value={form.due_date}
          onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>
      <TagInput tags={form.tags} setForm={setForm} />
      <Autocomplete
        multiple
        options={consortiumMembers || []}
        getOptionLabel={m => m.name || m.email || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        value={(consortiumMembers || []).filter(m => (form.assignees || []).includes(m.id))}
        onChange={(_, val) => setForm(f => ({ ...f, assignees: val.map(m => m.id) }))}
        renderInput={p => <TextField {...p} label="Assignees (optional)" />}
        renderTags={(val, getTagProps) => val.map((m, i) => (
          <Chip key={m.id} label={m.name || m.email || ''} size="small" {...getTagProps({ index: i })} />
        ))}
        sx={{ mb: 1.5 }}
        slotProps={{ popper: { style: { zIndex: 5000 } } }}
      />
      <TextField
        select fullWidth label="Linked section (optional)"
        value={form.linked_section} onChange={e => setForm(f => ({ ...f, linked_section: e.target.value }))}
        sx={{ mb: 2 }}
        SelectProps={{ MenuProps: { sx: { zIndex: 5000 } } }}
      >
        <MenuItem value="">— None —</MenuItem>
        {(sections || []).map(s => (
          <MenuItem key={s.id} value={s.id}>
            <Typography variant="body2" noWrap>{s.section_key}. {s.title}</Typography>
          </MenuItem>
        ))}
      </TextField>
      {/* a revision's to-dos always go on its own board: named, not chosen */}
      {showBoard && fixedBoardName && (
        <TextField fullWidth label="Board" value={fixedBoardName} sx={{ mb: 2 }}
          slotProps={{ input: { readOnly: true } }}
          helperText="Every to-do of this revision appears as a card on its board" />
      )}
      {showBoard && !fixedBoardName && (
        <TextField
          select fullWidth label="Board"
          value={form.board ?? TODO_LATEST_BOARD} onChange={e => setForm(f => ({ ...f, board: e.target.value }))}
          sx={{ mb: 2 }}
          helperText="The to-do also appears as a card on this board"
          SelectProps={{ MenuProps: { sx: { zIndex: 5000 } } }}
        >
          <MenuItem value={TODO_LATEST_BOARD}>Most recently active board</MenuItem>
          {(boards || []).map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
          <MenuItem value="">— No board —</MenuItem>
        </TextField>
      )}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={onSave} disabled={!form.title.trim()}>Save</Button>
      </Stack>
    </Box>
  );
}

function TodoItem({ todo, today, sectionById, onToggle, onEdit, onDelete, boards, onBoardChange }) {
  const p = TODO_PRIORITY[todo.priority] || TODO_PRIORITY.medium;
  const isOverdue = !todo.is_done && todo.due_date && todo.due_date < today;
  const section = todo.linked_section && sectionById[todo.linked_section];
  const assigneeNames = todo.assignee_names || [];
  return (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', 
    px: 1, py: 1.25, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' }, '&:hover .todo-acts': { opacity: 1 }, transition: 'border-color 0.15s' }}>
      <Checkbox
        checked={todo.is_done} onChange={onToggle}
        icon={<RadioButtonUncheckedIcon sx={{ fontSize: 22 }} />}
        checkedIcon={<CheckCircleOutlineIcon sx={{ fontSize: 22 }} />}
        sx={{ p: 0, mt: 0.15, flexShrink: 0, color: p.color, '&.Mui-checked': { color: 'text.disabled' } }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.6 }}>
          <Tooltip title={p.label} placement="top">
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: todo.is_done ? 'text.disabled' : p.color, flexShrink: 0, cursor: 'default' }} />
          </Tooltip>
          <Typography sx={{ 
                      textDecoration: todo.is_done ? 'line-through' : 'none', 
                      color: todo.is_done ? 'text.disabled' : 'text.primary', 
                      wordBreak: 'break-word', lineHeight: 1.4, 
                      fontSize: '1.25rem', fontWeight: todo.is_done ? 400 : 500 }}>
            {todo.title}
          </Typography>
        </Box>
        {/* Metadata row */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, pl: 2.25 }}>
          {todo.due_date && (
            <Chip
              size="small"
              label={`${isOverdue ? '⚠ Overdue · ' : ''}${todo.due_date}`}
              sx={{ height: 22, fontSize: '1.15rem', fontWeight: isOverdue ? 700 : 400, 
                bgcolor: isOverdue ? 'error.light' : 'action.selected', 
                color: isOverdue ? 'error.contrastText' : 'text.secondary', borderRadius: 1 }}
            />
          )}
          {section && (
            <Chip size="small" label={`§ ${section.section_key} · ${section.title}`} variant="outlined" sx={{ height: 22, fontSize: '1.15rem', maxWidth: 220, borderColor: 'divider' }} />
          )}
          {assigneeNames.map(a => (
            <Chip key={a.id} label={a.name || a.email} size="small" sx={{ height: 22, fontSize: '0.78rem', bgcolor: 'primary.light', color: 'primary.contrastText' }} />
          ))}
          {(todo.tags || []).map(tag => (
            <Chip key={tag} icon={<TagIcon sx={{ fontSize: '15px !important' }} />} label={tag} size="small" variant="outlined" sx={{ height: 22, fontSize: '1.15rem  ', color: 'text.secondary', borderColor: 'divider' }} />
          ))}
        </Box>
      </Box>
      {boards && (
        <TextField
          select size="small" variant="standard" value={todo.board || ''}
          onChange={e => onBoardChange(e.target.value)}
          onClick={e => e.stopPropagation()}
          sx={{ minWidth: 130, maxWidth: 170, flexShrink: 0, '& .MuiInputBase-input': { fontSize: '1.1rem' } }}
          SelectProps={{ displayEmpty: true, MenuProps: { sx: { zIndex: 5000 } } }}
          inputProps={{ 'aria-label': 'Board' }}
        >
          <MenuItem value=""><em>No board</em></MenuItem>
          {boards.map(b => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
        </TextField>
      )}
      <Box className="todo-acts" sx={{ display: 'flex', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0, gap: 0.25, mt: 0.15 }}>
        <IconButton size="small" onClick={onEdit}><EditIcon fontSize="small" /></IconButton>
        <IconButton size="small" onClick={onDelete} color="error"><DeleteIcon fontSize="small" /></IconButton>
      </Box>
    </Box>
  );
}

const TODO_LATEST_BOARD = '__latest__';

// reviewRoundId: a revision's to-dos only, created for that round (they go on its "Revision" board).
// zIndex: above full-screen overlays such as the reviews dialog.
export default function ProposalTodoPanel({ proposalId, sections, consortiumMembers, refreshKey, reviewRoundId = null,
  boardName = null, title = 'Todo List', emptyText = 'Track what still needs writing.', zIndex = 1600 }) {
  const [boards, setBoards] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(false);
  const EMPTY = { title: '', priority: 'medium', due_date: '', tags: [], assignees: [], linked_section: '' };
  const [form, setForm] = useState(EMPTY);
  const formRef = useRef(EMPTY);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [sortBy, setSortBy] = useState('default');
  const [filterTag, setFilterTag] = useState(null);

  const setFormAndRef = (updater) => {
    setForm(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      formRef.current = next;
      return next;
    });
  };

  // Reload when the proposal changes or after the Boards view was used (refreshKey)
  useEffect(() => {
    if (!proposalId) return;
    setLoading(true);
    axiosService.get('/proposal/todos/', { params: { proposal: proposalId, ...(reviewRoundId && { review_round: reviewRoundId }) } })
      .then(r => setTodos(r.data.results ?? r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetchBoards(proposalId).then(setBoards).catch(() => setBoards([]));
  }, [proposalId, refreshKey, reviewRoundId]);

  // Normalise only the fields being sent, so partial updates (e.g. ticking done)
  // never clear the other fields
  const cleanPayload = (p) => {
    const out = { ...p };
    if ('due_date' in p) out.due_date = p.due_date || null;
    if ('linked_section' in p) out.linked_section = p.linked_section || null;
    if ('tags' in p) out.tags = p.tags || [];
    if ('assignees' in p) out.assignees = p.assignees || [];
    if ('board' in p) {
      if (p.board === TODO_LATEST_BOARD) delete out.board; // server picks the latest board
      else out.board = p.board || null;
    }
    return out;
  };

  const apiCreate = async (payload) => {
    const r = await axiosService.post('/proposal/todos/', {
      ...cleanPayload(payload), proposal: proposalId, ...(reviewRoundId && { review_round: reviewRoundId }) });
    setTodos(prev => [...prev, r.data]);
  };
  const apiUpdate = async (id, payload) => {
    const r = await axiosService.patch(`/proposal/todos/${id}/`, cleanPayload(payload));
    setTodos(prev => prev.map(t => t.id === id ? r.data : t));
  };
  const apiDelete = async (id) => {
    await axiosService.delete(`/proposal/todos/${id}/`);
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const cancelForm = () => { setAdding(false); setEditingId(null); setFormAndRef(EMPTY); };

  const commitAdd = async () => {
    const snap = formRef.current;
    if (!snap.title.trim()) return;
    cancelForm();
    await apiCreate(snap);
  };
  const commitEdit = async () => {
    const snap = formRef.current;
    const id = editingId;
    cancelForm();
    await apiUpdate(id, snap);
  };
  const toggleDone = (todo) => apiUpdate(todo.id, { is_done: !todo.is_done });
  const changeBoard = (todo, boardId) => apiUpdate(todo.id, { board: boardId || null });
  const deleteTodo = (id) => apiDelete(id);
  const startEdit = (todo) => {
    setEditingId(todo.id);
    setAdding(false);
    setFormAndRef({
      title: todo.title,
      priority: todo.priority || 'medium',
      due_date: todo.due_date || '',
      tags: todo.tags || [],
      assignees: (todo.assignees || []).map(a => typeof a === 'object' ? a.id : a),
      linked_section: todo.linked_section || '',
    });
  };

  const today = new Date().toISOString().slice(0, 10);
  const sectionById = Object.fromEntries((sections || []).map(s => [s.id, s]));

  const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
  const allTags = [...new Set(todos.flatMap(t => t.tags || []))].sort();

  const applySort = (arr) => {
    const filtered = filterTag ? arr.filter(t => (t.tags || []).includes(filterTag)) : arr;
    switch (sortBy) {
      case 'due_date': return [...filtered].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
      case 'priority': return [...filtered].sort((a, b) =>
        (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1)
      );
      case 'tag': return [...filtered].sort((a, b) => {
        const ta = (a.tags || [])[0] || '';
        const tb = (b.tags || [])[0] || '';
        if (!ta && !tb) return 0;
        if (!ta) return 1;
        if (!tb) return -1;
        return ta.localeCompare(tb);
      });
      default: return [...filtered].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }
  };

  const groupByTag = (arr) => {
    const map = {};
    arr.forEach(todo => {
      const key = (todo.tags || [])[0] || '(no tag)';
      if (!map[key]) map[key] = [];
      map[key].push(todo);
    });
    return Object.entries(map).sort(([a], [b]) => a === '(no tag)' ? 1 : b === '(no tag)' ? -1 : a.localeCompare(b));
  };

  const pending = todos.filter(t => !t.is_done);
  const done    = todos.filter(t => t.is_done);
  const overdue = pending.filter(t => t.due_date && t.due_date < today).length;

  const handleClose = () => { cancelForm(); setDialogOpen(false); };

  const formProps = { form, setForm: setFormAndRef, sections, consortiumMembers, boards, fixedBoardName: boardName };

  /* ---- compact sidebar panel ---- */
  const recentTodos = [...todos].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).slice(0, 5);

  return (
    <>
      <Paper
        variant="outlined"
        onClick={() => setDialogOpen(true)}
        sx={{ borderRadius: 1, mt: 2, pb: 2, cursor: 'pointer', overflow: 'hidden', 
            transition: 'border-color 0.15s', '&:hover': { borderColor: 'primary.xxulight' } }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', 
                justifyContent: 'space-between', 
                borderBottom: recentTodos.length ? '1px solid' : 'none', 
                borderColor: 'divider',  }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TaskIcon fontSize="small" color={overdue ? 'error' : 'action'} />
            <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
            {pending.length > 0 && (
              <Chip label={`${pending.length}`} size="small" color={overdue ? 'error' : 'primary'} sx={{ height: 18, fontSize: '1.1rem', fontWeight: 700 }} />
            )}
          </Box>
          <ExpandMoreIcon fontSize="small" color="action" />
        </Box>

        {/* Last 5 todos preview */}
        {recentTodos.length > 0 && (
          <Stack sx={{ px: 1.5, py: 1 }} spacing={0}>
            {recentTodos.map(todo => {
              const p = TODO_PRIORITY[todo.priority] || TODO_PRIORITY.medium;
              const isOverdueTodo = !todo.is_done && todo.due_date && todo.due_date < today;
              const section = todo.linked_section && sectionById[todo.linked_section];
              return (
                <Box key={todo.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                  <Box sx={{ mt: 0.5, width: 8, height: 8, borderRadius: '50%', flexShrink: 0, bgcolor: todo.is_done ? 'text.disabled' : p.color, opacity: todo.is_done ? 0.5 : 1 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" noWrap sx={{ fontSize: '1.25rem', textDecoration: todo.is_done ? 'line-through' : 'none', color: todo.is_done ? 'text.disabled' : 'text.primary', fontWeight: 500 }}>
                      {todo.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.3 }}>
                      {todo.due_date && (
                        <Chip
                          size="small"
                          label={`${isOverdueTodo ? '⚠ ' : ''}${todo.due_date}`}
                          sx={{ height: 16, fontSize: '0.95rem', fontWeight: isOverdueTodo ? 700 : 400, bgcolor: isOverdueTodo ? 'error.light' : 'action.selected', color: isOverdueTodo ? 'error.contrastText' : 'text.secondary', borderRadius: 0.75 }}
                        />
                      )}
                      {section && (
                        <Chip size="small" label={`§ ${section.section_key}: ${section.title}`} variant="outlined" 
                            sx={{ height: 16, fontSize: '0.95rem', borderColor: 'divider', color: 'text.secondary' }} />
                      )}
                   
                      {(todo.tags || []).slice(0, 2).map(tag => (
                        <Chip key={tag} icon={<TagIcon sx={{ fontSize: '10px !important' }} />} 
                        label={tag} size="small" variant="outlined" 
                        sx={{ height: 16, fontSize: '0.95rem', color: 'text.disabled', borderColor: 'divider' }} />
                      ))}

                    {todo.board_name && (
                        <Chip label={todo.board_name} size="small" variant="outlined"
                          sx={{ height: 16, fontSize: '0.95rem', borderColor: 'primary.light', color: 'primary.main' }} />
                      )}
                    {(todo.assignee_names || []).slice(0, 2).map(a => (
                        <Chip key={a.id} label={a.name?.split(' ')[0] || a.email} size="small" 
                        sx={{ height: 16, fontSize: '0.95rem', bgcolor: 'primary.light', color: 'primary.contrastText' }} />
                      ))}
                    </Box>
                  </Box>
                </Box>
              );
            })}
            {todos.length > 5 && (
              <Typography variant="caption" color="text.disabled" sx={{ pt: 0.75, display: 'block', textAlign: 'center', fontSize: '0.7rem' }}>
                +{todos.length - 5} more
              </Typography>
            )}
          </Stack>
        )}

        {todos.length === 0 && (
          <Typography variant="body2" color="text.disabled" sx={{ px: 2, py: 1.5, fontSize: '1.25rem', textAlign: 'center' }}>
            No todos yet — click to add
          </Typography>
        )}
      </Paper>

      {/* ---- full-tab dialog ---- */}
      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="md" sx={{ zIndex }} 
                PaperProps={{ sx: { borderRadius: 1, height: '88vh', 
                                    display: 'flex', flexDirection: 'column' } 
                            }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TaskIcon color={overdue ? 'error' : 'primary'} />
            <Typography variant="h6" fontWeight={700}>{title}</Typography>
            {pending.length > 0 && <Chip label={`${pending.length} pending`} size="small" color={overdue ? 'error' : 'primary'} />}
            {overdue > 0 && <Chip label={`${overdue} overdue`} size="small" color="error" variant="outlined" />}
          </Box>
          <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />

        {/* Sort + tag filter toolbar */}
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', 
                   gap: 1.5, flexWrap: 'wrap', borderBottom: '1px solid', 
                   borderColor: 'divider', bgcolor: 'background.default' }}>
          <TextField
            select size="small" label="Sort by" value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            sx={{ minWidth: 140 }}
            SelectProps={{ MenuProps: { sx: { zIndex: zIndex + 100 } } }}
          >
            <MenuItem value="default">Newest first</MenuItem>
            <MenuItem value="due_date">Due date</MenuItem>
            <MenuItem value="priority">Priority</MenuItem>
            <MenuItem value="tag">Group by tag</MenuItem>
          </TextField>
          {allTags.length > 0 && (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25, fontSize: '1.15rem' }}>Filter:</Typography>
              {allTags.map(tag => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  icon={<TagIcon sx={{ fontSize: '13px !important' }} />}
                  onClick={() => setFilterTag(filterTag === tag ? null : tag)}
                  color={filterTag === tag ? 'primary' : 'default'}
                  variant={filterTag === tag ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
              {filterTag && (
                <Chip label="Clear" size="small" onClick={() => setFilterTag(null)} sx={{ cursor: 'pointer' }} />
              )}
            </Box>
          )}
        </Box>

        <DialogContent sx={{ flex: 1, overflowY: 'auto', pt: 2 }}>
          {/* Add button */}
          {!adding && !editingId && (
            <Button startIcon={<AddIcon />} onClick={() => setAdding(true)} fullWidth
              variant="outlined" sx={{ mb: 2, borderStyle: 'dashed', 
                                       fontSize: '1.25rem'
               }}>
              Add todo
            </Button>
          )}
          {adding && <TodoForm {...formProps} showBoard onSave={commitAdd} onCancel={cancelForm} />}

          {/* Pending */}
          {(() => {
            const sorted = applySort(pending);
            if (sorted.length === 0 && !adding) return null;
            if (sortBy === 'tag') {
              return (
                <Box sx={{ mb: 2 }}>
                  {groupByTag(sorted).map(([tag, items]) => (
                    <Box key={tag} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <TagIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                        <Typography variant="body2" fontWeight={700} sx={{ 
                            color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {tag}
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                      </Box>
                      <Stack spacing={1}>
                        {items.map(todo => (
                          editingId === todo.id
                            ? <TodoForm key={todo.id} {...formProps} onSave={commitEdit} onCancel={cancelForm} />
                            : <TodoItem key={todo.id} todo={todo} today={today} sectionById={sectionById}
                                onToggle={() => toggleDone(todo)} onEdit={() => startEdit(todo)} onDelete={() => deleteTodo(todo.id)}
                        boards={boards} onBoardChange={(boardId) => changeBoard(todo, boardId)} />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Box>
              );
            }
            return (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {sorted.map(todo => (
                  editingId === todo.id
                    ? <TodoForm key={todo.id} {...formProps} onSave={commitEdit} onCancel={cancelForm} />
                    : <TodoItem key={todo.id} todo={todo} today={today} sectionById={sectionById}
                        onToggle={() => toggleDone(todo)} onEdit={() => startEdit(todo)} onDelete={() => deleteTodo(todo.id)}
                        boards={boards} onBoardChange={(boardId) => changeBoard(todo, boardId)} />
                ))}
              </Stack>
            );
          })()}

          {/* Done */}
          {(() => {
            const sortedDone = applySort(done);
            const sortedPending = applySort(pending);
            return (
              <>
                {sortedDone.length > 0 && (
                  <>
                    <Divider sx={{ my: 2 }}>
                      <Typography variant="caption" color="text.disabled">Completed ({sortedDone.length})</Typography>
                    </Divider>
                    <Stack spacing={1} sx={{ opacity: 0.6 }}>
                      {sortedDone.map(todo => (
                        <TodoItem key={todo.id} todo={todo} today={today} sectionById={sectionById}
                          onToggle={() => toggleDone(todo)} onEdit={() => startEdit(todo)} onDelete={() => deleteTodo(todo.id)}
                        boards={boards} onBoardChange={(boardId) => changeBoard(todo, boardId)} />
                      ))}
                    </Stack>
                  </>
                )}
                {todos.length === 0 && !adding && (
                  <Box sx={{ py: 6, textAlign: 'center' }}>
                    <TaskIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography color="text.secondary">No todos yet. {emptyText}</Typography>
                  </Box>
                )}
                {filterTag && sortedPending.length === 0 && sortedDone.length === 0 && todos.length > 0 && (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No todos tagged "{filterTag}".
                  </Typography>
                )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </>
  );
}
